import React, { useState } from 'react';
import { COUNTRIES } from '../../../constants/countries';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';

interface CountrySelectorProps {
  selectedCountries: string[];
  onCountriesChange: (countries: string[]) => void;
}

export const CountrySelector: React.FC<CountrySelectorProps> = ({ 
  selectedCountries, 
  onCountriesChange 
}) => {
  const [countrySearch, setCountrySearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredCountries = COUNTRIES.filter(country =>
    country.toLowerCase().includes(countrySearch.toLowerCase()) &&
    !selectedCountries.includes(country)
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Input
          id="country-search-input"
          inputSize="sm"
          fullWidth
          value={countrySearch}
          onChange={(e) => { setCountrySearch(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder="Search countries..."
          aria-label="Search countries"
          aria-expanded={showDropdown && filteredCountries.length > 0}
          aria-haspopup="listbox"
        />
        {showDropdown && filteredCountries.length > 0 && (
          <div 
            role="listbox" 
            aria-label="Country options"
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-40 overflow-y-auto z-10"
          >
            {filteredCountries.slice(0, 8).map((country) => (
              <button
                key={country}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => { 
                  onCountriesChange([...selectedCountries, country]); 
                  setCountrySearch(''); 
                  setShowDropdown(false); 
                }}
                className="w-full text-left px-3 py-2 text-[13px] text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                {country}
              </button>
            ))}
          </div>
        )}
      </div>
      {selectedCountries.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedCountries.map((country) => (
            <Badge key={country} variant="primary-light" size="sm">
              {country}
              <button
                type="button"
                onClick={() => onCountriesChange(selectedCountries.filter(c => c !== country))}
                className="ml-1 text-primary-500 hover:text-primary-700"
                aria-label={`Remove ${country}`}
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
